/**
 * The site-wide campus selection, shared with the campus-picker widget.
 * See the `campus-aware-widget` skill: primary cookie is JSON, legacy cookie
 * is base64 from the old TBX header, and same-page changes arrive as a
 * `campus-picker:changed` CustomEvent on window.
 *
 * This is a copy of campus-picker's campus-storage.ts rather than an import
 * so the two bundles stay independent.
 */

const COOKIE_NAME = 'woodside-selected-campus';
const LEGACY_COOKIE = 'tbx-ws__selected-location';
const COOKIE_DOMAIN = '.woodsidebible.org';
const COOKIE_MAX_AGE = 60 * 60 * 24 * 365;

export interface SelectedCampus {
  congregationId: number;
  name: string;
  slug: string;
  svgUrl: string | null;
}

function readCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  const match = document.cookie
    .split(';')
    .map((c) => c.trim())
    .find((c) => c.startsWith(`${name}=`));
  if (!match) return null;
  try {
    return decodeURIComponent(match.slice(name.length + 1));
  } catch {
    return match.slice(name.length + 1);
  }
}

export function getSelectedCampusId(): number | null {
  try {
    const raw = readCookie(COOKIE_NAME);
    if (raw) {
      const data = JSON.parse(raw);
      if (data?.congregationId) return Number(data.congregationId);
    }
  } catch {
    /* fall through to legacy */
  }
  try {
    const raw = readCookie(LEGACY_COOKIE);
    if (!raw) return null;
    const data = JSON.parse(atob(raw));
    return data?.location_id ? Number(data.location_id) : null;
  } catch {
    return null;
  }
}

export function onCampusChange(handler: (congregationId: number) => void): () => void {
  function listener(e: Event) {
    const detail = (e as CustomEvent).detail;
    if (detail?.congregationId) handler(Number(detail.congregationId));
  }
  window.addEventListener('campus-picker:changed', listener);
  return () => window.removeEventListener('campus-picker:changed', listener);
}

function cookieDomain(): string {
  const host = window.location.hostname;
  if (host === 'localhost' || host === '127.0.0.1') return '';
  if (host.endsWith('woodsidebible.org')) return COOKIE_DOMAIN;
  const parts = host.split('.');
  return parts.length >= 2 ? '.' + parts.slice(-2).join('.') : '';
}

function writeCookie(name: string, value: string): void {
  try {
    const domain = cookieDomain();
    let cookie = `${name}=${value};path=/;max-age=${COOKIE_MAX_AGE};SameSite=Lax`;
    if (domain) cookie += `;domain=${domain}`;
    document.cookie = cookie;
  } catch {
    /* cookie blocked */
  }
}

/** Persist a campus choice site-wide and tell every other widget on the page. */
export function setSelectedCampus(campus: SelectedCampus): void {
  writeCookie(COOKIE_NAME, encodeURIComponent(JSON.stringify(campus)));
  try {
    const legacy = btoa(
      JSON.stringify({
        location_id: campus.congregationId,
        location_name: campus.name,
        location_short_name: campus.name,
        location_url: `https://woodsidebible.org/locations/${campus.slug}/`,
      })
    );
    writeCookie(LEGACY_COOKIE, encodeURIComponent(legacy));
  } catch {
    /* btoa on odd input */
  }
  window.dispatchEvent(new CustomEvent('campus-picker:changed', { detail: campus }));
}

/** The MP widget login token, if the visitor signed in through the site header. */
export function getMpWidgetToken(): string | null {
  try {
    const token = localStorage.getItem('mpp-widgets_AuthToken');
    return token && token !== 'null' ? token : null;
  } catch {
    return null;
  }
}

/** "Farmington Hills Campus" -> "farmington-hills", matching the site's campus slugs. */
export function campusSlug(name: string): string {
  return name
    .replace(/\s+campus$/i, '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
}

/**
 * Ask the host page's `<mpp-user-login>` element to open MP's login modal.
 * After a successful login MP writes the token to localStorage, which
 * `onMpTokenChange` picks up. Returns false when the page has no login
 * element (the caller can fall back to a link).
 */
export function triggerMpLogin(): boolean {
  try {
    const el = document.querySelector('mpp-user-login') as HTMLElement | null;
    if (!el) return false;
    // Since the login widget took over the header, the stock element is kept
    // only for the OAuth callback and is visually hidden. Its modal would
    // open inside that hidden box, so treat it as absent and use MP's hosted
    // sign-in instead.
    const box = el.getBoundingClientRect();
    if (box.width < 2 || box.height < 2) return false;
    const clickable =
      (el.shadowRoot?.querySelector('a, button') as HTMLElement | null) ||
      (el.querySelector('a, button') as HTMLElement | null) ||
      el;
    clickable.click();
    return true;
  } catch {
    return false;
  }
}

/** Fire when the MP widget token appears, changes or goes away (login modal, another tab, expiry). */
export function onMpTokenChange(handler: (token: string | null) => void): () => void {
  let last = getMpWidgetToken();
  const check = () => {
    const cur = getMpWidgetToken();
    if (cur !== last) {
      last = cur;
      handler(cur);
    }
  };
  const onStorage = (e: StorageEvent) => {
    if (e.key === null || e.key === 'mpp-widgets_AuthToken') check();
  };
  window.addEventListener('storage', onStorage);
  window.addEventListener('focus', check);
  const interval = window.setInterval(check, 1500);
  return () => {
    window.removeEventListener('storage', onStorage);
    window.removeEventListener('focus', check);
    window.clearInterval(interval);
  };
}

/**
 * MP's hosted sign-in, coming straight back to this page afterwards. This is
 * the same URL the old details page's forceLogin.js built, so the widget
 * login flow (`signin-oidc` on my.woodsidebible.org) lands the token in
 * localStorage and returns to `state`. `mpCustomWidgetAuth=true` on the
 * return URL is what MP's own widgets look for; harmless to us.
 */
export function mpLoginUrl(returnTo: string = window.location.href): string {
  const host = 'my.woodsidebible.org';
  const state = returnTo + (returnTo.includes('?') ? '&' : '?') + 'mpCustomWidgetAuth=true';
  const nonce = Array.from(crypto.getRandomValues(new Uint8Array(16)), (b) =>
    b.toString(16).padStart(2, '0')
  ).join('');
  return (
    `https://${host}/ministryplatformapi/oauth/connect/authorize?response_type=code` +
    `&scope=openid%20http://www.thinkministry.com/dataplatform/scopes/all&client_id=TM.Widgets` +
    `&redirect_uri=https://${host}/widgets/signin-oidc&nonce=${nonce}&state=${encodeURIComponent(state)}`
  );
}

/** Open the header's login modal when the page has one; otherwise go to MP's sign-in and come back. */
export function signInAndReturn(): void {
  if (!triggerMpLogin()) window.location.assign(mpLoginUrl());
}

/**
 * Sign out exactly the way the stock `<mpp-user-login>` does: ask the widget
 * API where MP's end-session endpoint is, clear the three localStorage keys
 * to the string 'null' (what the stock widget stores), then send the
 * browser through `endsession` with the id token and back to this page.
 * If the config fetch fails, the keys are still cleared and the page
 * reloads signed out.
 */
export async function signOut(): Promise<void> {
  let idToken: string | null = null;
  try {
    idToken = localStorage.getItem('mpp-widgets_IdToken');
    if (idToken === 'null') idToken = null;
  } catch {
    /* storage blocked */
  }
  interface AuthConfig {
    signOutUrl?: string;
    postLogoutRedirectUrl?: string;
  }
  let config: AuthConfig | null = null;
  try {
    const res = await fetch('https://my.woodsidebible.org/widgets/Api/Auth');
    if (res.ok) config = (await res.json()) as AuthConfig;
  } catch {
    /* fall through */
  }
  try {
    localStorage.setItem('mpp-widgets_AuthToken', 'null');
    localStorage.setItem('mpp-widgets_IdToken', 'null');
    localStorage.setItem('mpp-widgets_ExpiresAfter', 'null');
  } catch {
    /* storage blocked */
  }
  if (config?.signOutUrl && config.postLogoutRedirectUrl && idToken) {
    window.location.replace(
      `${config.signOutUrl}?id_token_hint=${idToken}` +
        `&post_logout_redirect_uri=${encodeURIComponent(config.postLogoutRedirectUrl)}` +
        `&state=${encodeURI(window.location.href)}`
    );
    return;
  }
  window.location.reload();
}
